import { getCachedEmailLists, mergeWithDefaultBlacklist } from '@/services/emailLists';
import { CountryCodes } from './CountryCodes';

/** Dominios de correo gratuito que se rechazan por defecto en validación business */
const DEFAULT_FREE_EMAIL_SLDS = [
  'gmail',
  'yahoo',
  'hotmail',
  'outlook',
  'live',
  'msn',
  'icloud',
  'aol',
  'mail',
  'protonmail',
  'zoho',
  'yandex',
  'gmx',
  'tutanota',
  'fastmail',
  'mailfence',
];

const ACADEMIC_WHITELIST_SLDS = [
  'disgenet',
  'aryavaidyasala',
  'gxyyzwy',
  'arvindgavalipharmacycollege',
  'kkumail',
  'iisgm',
  'researchmar',
];

/** Dominios siempre permitidos (signup y academic), sin depender del whitelist remoto. */
const DEFAULT_WHITELIST_DOMAINS = ['techtitute.com', 'student.techtitute.com'];

function getLists() {
  const lists = getCachedEmailLists();
  return {
    whitelist: lists.whitelist ?? [],
    blacklist: mergeWithDefaultBlacklist(lists.blacklist ?? []),
  };
}

function normalizeWhitelistEntry(entry: string) {
  return String(entry || '')
    .trim()
    .toLowerCase()
    .replace(/^\*@/, '')
    .replace(/^@/, '');
}

function domainMatchesWhitelistEntry(domain: string | undefined, entry: string) {
  const normalized = normalizeWhitelistEntry(entry);
  if (!domain || !normalized || normalized.includes('@')) return false;
  return domain === normalized || domain.endsWith(`.${normalized}`);
}

function isEmailWhitelisted(email: string, whitelist: string[] = []) {
  const cleaned = email?.trim()?.toLowerCase();
  if (!cleaned || !Array.isArray(whitelist) || whitelist.length === 0) return false;

  const domain = cleaned.split('@')[1];

  return whitelist.some(entry => {
    const raw = String(entry || '')
      .trim()
      .toLowerCase();
    if (!raw) return false;
    if (raw === cleaned) return true;
    return domainMatchesWhitelistEntry(domain, raw);
  });
}

function isWhitelistedAcademicDomain(domain: string | undefined) {
  if (!domain) return false;
  const sld = domain.split('.')[0];
  if (ACADEMIC_WHITELIST_SLDS.includes(sld)) return true;
  return DEFAULT_WHITELIST_DOMAINS.some(allowed => domain === allowed || domain.endsWith(`.${allowed}`));
}

function validateEmailBusiness(email, lists = null) {
  const { whitelist, blacklist } = lists ?? getLists();
  const domain = email.split('@')[1];
  const sld = domain ? domain.split('.')[0] : null;

  if (isEmailWhitelisted(email, [...DEFAULT_WHITELIST_DOMAINS, ...whitelist])) return true;
  const effectiveBlacklist = [...(blacklist || []), ...DEFAULT_FREE_EMAIL_SLDS];
  if (effectiveBlacklist.includes(sld)) return false;
  if (effectiveBlacklist.includes(domain)) return false;
  return true;
}

function validateEmailAcademic(email, lists = null) {
  const { whitelist, blacklist } = lists ?? getLists();
  const allowedTld = ['edu', 'gov', 'org', 'eu', 'education', ...CountryCodes];
  const domain = email?.split('@')[1];
  const sld = domain ? domain.split('.')[0] : null;
  const tld = domain?.split('.').slice(-2).join('.');
  const simpleTld = domain?.split('.').pop();

  if (isEmailWhitelisted(email, [...DEFAULT_WHITELIST_DOMAINS, ...whitelist])) return true;
  if (isWhitelistedAcademicDomain(domain)) return true;
  if (allowedTld.includes(tld) || allowedTld.includes(simpleTld)) return true;
  if (blacklist.includes(sld)) return false;
  if (blacklist.includes(domain)) return false;
  if (/^edu\.[a-z]+$/.test(tld)) return true;
  return false;
}

function validateEmail(email, isAcademic = false, lists = null) {
  const cleanedEmail = email?.trim()?.toLowerCase();
  const isValidFormat = cleanedEmail?.match(
    /^([a-zA-Z0-9_\-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,20}|[0-9]{1,3})(\]?)$/,
  );
  if (!isValidFormat) return false;
  const isValidDomain = isAcademic
    ? validateEmailAcademic(cleanedEmail, lists)
    : validateEmailBusiness(cleanedEmail, lists) || validateEmailAcademic(cleanedEmail, lists);
  return isValidDomain;
}
function validateContactEmail(value) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}

function validateNotEmpty(inputValue) {
  return inputValue?.trim() !== '';
}

/** Dominios comerciales/internos que no deben tratarse como académicos al reservar demo */
const BOOK_DEMO_COMMERCIAL_SLDS = ['disgenet'];

type EmailLists = { whitelist: string[]; blacklist: string[] };

function hasAcademicTld(domain: string) {
  const parts = domain.split('.');
  const simpleTld = parts[parts.length - 1];
  const secondLevel = parts.length >= 2 ? parts[parts.length - 2] : '';

  if (simpleTld === 'edu' || simpleTld === 'ac' || secondLevel === 'edu' || secondLevel === 'ac') {
    return true;
  }
  return /^edu\.[a-z]+$/.test(parts.slice(-2).join('.'));
}

function matchesBlacklistEntry(email: string, domain: string, sld: string, entry: string) {
  if (email === entry || domain === entry || sld === entry) return true;
  if (domain.endsWith(`.${entry}`)) return true;
  if (entry === 'vip' && (sld === 'vip' || domain.startsWith('vip.'))) return true;
  return false;
}

function isListedInRemoteBlacklist(email: string, domain: string, sld: string, blacklist: string[]) {
  return blacklist.some(entry => matchesBlacklistEntry(email, domain, sld, entry));
}

function isBookDemoBlockedEmail(email: string, lists: EmailLists | null = null) {
  const cleaned = email?.trim()?.toLowerCase();
  if (!cleaned || !validateContactEmail(cleaned)) return true;

  const domain = cleaned.split('@')[1];
  if (!domain) return true;

  const sld = domain.split('.')[0];
  const sourceLists = lists ?? getLists();
  const whitelist = sourceLists.whitelist ?? [];
  const blacklist = mergeWithDefaultBlacklist(sourceLists.blacklist ?? []);

  if (isEmailWhitelisted(cleaned, whitelist)) return false;
  if (BOOK_DEMO_COMMERCIAL_SLDS.includes(sld)) return false;
  if (hasAcademicTld(domain)) return true;
  if (isListedInRemoteBlacklist(cleaned, domain, sld, blacklist)) return true;
  if (validateEmailAcademic(cleaned, lists)) return true;

  return false;
}

function isAcademicEmailForBookDemo(email: string, lists: EmailLists | null = null) {
  return isBookDemoBlockedEmail(email, lists);
}

export {
  validateEmail,
  validateContactEmail,
  validateNotEmpty,
  validateEmailAcademic,
  validateEmailBusiness,
  isBookDemoBlockedEmail,
  isAcademicEmailForBookDemo,
};
